import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, EyeOff, Globe, Package, Pencil, Plus, Sparkles, Trash2, Warehouse } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  CompactStatCard,
  DataTable,
  FormInput,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import { channelStockService, type ChannelStockAdminItem } from "@/services/channelStockService";
import { channelProductsService } from "@/services/channelProductsService";

const QUERY_KEY = ["admin", "channel-products"];

const SEARCH_KEYS: (keyof ChannelStockAdminItem)[] = ["productName", "sku", "storeName"];

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "visibility",
    placeholder: "Status",
    options: [
      { label: "All status", value: "all" },
      { label: "In catalogue", value: "published" },
      { label: "Hidden", value: "hidden" },
      { label: "Out of stock", value: "out" },
    ],
  },
];

const StatusBadge = ({ item }: { item: ChannelStockAdminItem }) =>
  item.isPublished ? (
    <Badge variant="outline" className="bg-success/10 text-success border-success/20">In catalogue</Badge>
  ) : (
    <Badge variant="outline">Hidden</Badge>
  );

const AdminChannelProducts = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [stockTarget, setStockTarget] = useState<ChannelStockAdminItem | null>(null);
  const [stockValue, setStockValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ChannelStockAdminItem | null>(null);

  const { data = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => channelStockService.listAdmin(),
  });

  const table = useDataTable<ChannelStockAdminItem>({
    data,
    searchKeys: SEARCH_KEYS,
    filterConfigs: statusFilters,
    filterPredicate: (row, state) => {
      const filter = state.filters.visibility;
      if (filter === "published") return row.isPublished;
      if (filter === "hidden") return !row.isPublished;
      if (filter === "out") return row.adminQuantity <= 0;
      return true;
    },
  });

  const stats = useMemo(
    () => ({
      total: data.length,
      published: data.filter((p) => p.isPublished).length,
      hidden: data.filter((p) => !p.isPublished).length,
      out: data.filter((p) => p.adminQuantity <= 0).length,
    }),
    [data],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const stockMutation = useMutation({
    mutationFn: ({ productId, quantity }: { productId: number; quantity: number }) =>
      channelStockService.setAdmin(productId, quantity),
    onSuccess: () => {
      toast({ title: `${stockTarget?.productName} stock updated` });
      refresh();
      setStockTarget(null);
    },
    onError: (err) => {
      toast({ title: "Could not update stock", description: getApiErrorMessage(err), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (productId: number) => channelProductsService.delete(productId),
    onSuccess: () => {
      toast({ title: `${deleteTarget?.productName} deleted` });
      refresh();
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast({ title: "Could not delete product", description: getApiErrorMessage(err), variant: "destructive" });
      setDeleteTarget(null);
    },
  });

  const cleanDuplicatesMutation = useMutation({
    mutationFn: () => channelProductsService.cleanDuplicates(),
    onSuccess: () => {
      toast({ title: "Duplicates cleaned" });
      refresh();
    },
    onError: (err) => {
      toast({ title: "Clean duplicates failed", description: getApiErrorMessage(err), variant: "destructive" });
    },
  });

  const openStock = (item: ChannelStockAdminItem) => {
    setStockTarget(item);
    setStockValue(String(item.adminQuantity));
  };

  const editHref = (item: ChannelStockAdminItem) => `/admin/channel-products/${item.productId}/edit`;

  const columns: DataTableColumn<ChannelStockAdminItem>[] = [
    {
      id: "productName",
      header: "Product",
      sortable: true,
      mobilePriority: 1,
      cell: (item) => (
        <div className="min-w-0">
          <Link to={editHref(item)} className="font-semibold hover:text-primary hover:underline">
            {item.productName}
          </Link>
          {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
        </div>
      ),
    },
    {
      id: "customerPrice",
      header: "Customer price",
      sortable: true,
      align: "right",
      mobileLabel: "Customer price",
      cell: (item) => <span className="font-medium">₹{item.customerPrice}</span>,
    },
    {
      id: "commission",
      header: "Commission",
      mobileLabel: "Commission",
      cell: (item) => (
        <span className="text-xs text-muted-foreground">
          D {item.distributorCommissionPercent}% · W {item.wholesaleCommissionPercent}% · R {item.retailCommissionPercent}%
        </span>
      ),
    },
    {
      id: "adminQuantity",
      header: "Admin stock",
      sortable: true,
      align: "right",
      mobileLabel: "Admin stock",
      cell: (item) => (
        <span className={item.adminQuantity <= 0 ? "font-semibold text-destructive" : "font-medium"}>
          {item.adminQuantity}
        </span>
      ),
    },
    {
      id: "isPublished",
      header: "Status",
      sortable: true,
      mobileLabel: "Status",
      cell: (item) => <StatusBadge item={item} />,
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      hideOnMobile: true,
      cell: (item) => (
        <div className="flex items-center justify-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" title="Set stock" onClick={() => openStock(item)}>
            <Warehouse className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" asChild title="Edit">
            <Link to={editHref(item)}><Pencil className="h-4 w-4" /></Link>
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-destructive"
            title="Delete"
            onClick={() => setDeleteTarget(item)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const parsedStock = stockValue === "" ? null : Number(stockValue);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Channel Products"
        description="Admin-owned products for the distribution network (Distributors, Wholesalers, Retailers), managed separately from store products."
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Button
              variant="outline"
              disabled={cleanDuplicatesMutation.isPending || isLoading}
              onClick={() => cleanDuplicatesMutation.mutate()}
            >
              <Sparkles className="mr-1.5 h-4 w-4 text-amber-500" />
              {cleanDuplicatesMutation.isPending ? "Cleaning…" : "Clean duplicates"}
            </Button>
            <Button asChild>
              <Link to="/admin/channel-products/new">
                <Plus className="mr-1.5 h-4 w-4" /> Add product
              </Link>
            </Button>
          </div>
        }
      />

      <StatsGrid columns={4}>
        <CompactStatCard title="Total products" value={isLoading ? "…" : stats.total} icon={Package} />
        <CompactStatCard title="In catalogue" value={isLoading ? "…" : stats.published} icon={Globe} />
        <CompactStatCard title="Hidden" value={isLoading ? "…" : stats.hidden} icon={EyeOff} />
        <CompactStatCard title="Out of stock" value={isLoading ? "…" : stats.out} icon={Boxes} />
      </StatsGrid>

      {isLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : isError ? (
        <div className="space-y-3 rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="text-sm text-destructive">{getApiErrorMessage(error, "Failed to load channel products")}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <DataTable
          title={`All Channel Products (${data.length})`}
          columns={columns}
          data={table.rows}
          getRowId={(row) => String(row.productId)}
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder="Search products…"
          filters={statusFilters}
          filterValues={table.state.filters}
          onFilterChange={table.setFilter}
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          emptyMessage="No channel products yet. Use “Add product” to create the first one."
          renderMobileCard={(item) => (
            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{item.productName}</p>
                  {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
                </div>
                <StatusBadge item={item} />
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-bold">₹{item.customerPrice}</span>
                <span className={item.adminQuantity <= 0 ? "text-destructive" : "text-muted-foreground"}>
                  • Stock {item.adminQuantity}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Distributor {item.distributorCommissionPercent}% · Wholesale {item.wholesaleCommissionPercent}% · Retail {item.retailCommissionPercent}%
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="min-w-[80px] flex-1" onClick={() => openStock(item)}>
                  <Warehouse className="mr-1 h-3 w-3" /> Stock
                </Button>
                <Button size="sm" variant="outline" className="min-w-[80px] flex-1" asChild>
                  <Link to={editHref(item)}><Pencil className="mr-1 h-3 w-3" /> Edit</Link>
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => setDeleteTarget(item)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        />
      )}

      <Sheet open={stockTarget !== null} onOpenChange={(open) => !open && setStockTarget(null)}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle>Set admin stock</SheetTitle>
            <SheetDescription>{stockTarget?.productName}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 space-y-4 px-6 py-5">
            <FormInput
              label="Quantity in admin stock"
              inputMode="numeric"
              value={stockValue}
              onChange={(e) => setStockValue(e.target.value.replace(/\D/g, ""))}
              hint={stockTarget ? `Currently ${stockTarget.adminQuantity}. Distributors order from this stock.` : undefined}
            />
          </div>
          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setStockTarget(null)}>Cancel</Button>
            <Button
              disabled={
                stockMutation.isPending || parsedStock === null || parsedStock === stockTarget?.adminQuantity
              }
              onClick={() =>
                stockTarget && parsedStock !== null && stockMutation.mutate({ productId: stockTarget.productId, quantity: parsedStock })
              }
            >
              {stockMutation.isPending ? "Saving…" : "Save stock"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.productName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the product from Channel Products. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.productId)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminChannelProducts;
